import { useApiStore } from "@/Hub/Stores/apiStore";
import gateway from "@/Hub/Gateway/Gateway";
import { URL_API } from "@/config/domains";

// Проверка что мы в браузере
const isClient = typeof window !== "undefined";

type APIEndpoint = {
  response: { status?: number };
} & ({ request: any } | { query: any });

type APIRequestMethod = {
  [K in keyof API]: API[K] extends APIEndpoint ? K : never;
}[keyof API];

type APIRequest<T extends APIRequestMethod> = Extract<
  API[T],
  APIEndpoint
> extends { request: infer Request }
  ? Request
  : Extract<API[T], APIEndpoint> extends { query: infer Query }
    ? Query
    : never;

type APIResponse<T extends APIRequestMethod> = Extract<
  API[T],
  APIEndpoint
>["response"];

type APISuccessResponse<T extends APIRequestMethod> = Extract<
  APIResponse<T>,
  { status: 200 | 201 }
> extends never
  ? APIResponse<T>
  : Extract<APIResponse<T>, { status: 200 | 201 }>;

const apiCall = async <T extends APIRequestMethod>(
  method: T,
  request: APIRequest<T>,
): Promise<APISuccessResponse<T>> => {
  const url = `${URL_API}/${method}`;
  const methodName = method as string;
  const isAuthEndpoint = methodName.startsWith("auth/");
  let shouldUseGateway =
    isClient && !isAuthEndpoint && (gateway.connected || gateway.preferGateway);
  const startTime = performance.now();

  // Log API call start
  let recordId: string | undefined;
  if (isClient) {
    try {
      recordId = useApiStore.getState().addRecord({
        endpoint: method,
        method: "POST",
        request,
        status: "pending",
      });
    } catch (e) {
      // Silently fail if store not available
    }
  }

  try {
    if (shouldUseGateway && !gateway.connected) {
      try {
        await gateway.connect();
      } catch {
        shouldUseGateway = false;
      }
    }

    if (shouldUseGateway && gateway.connected) {
      const responseData = await gateway.request(method, request);
      const duration = performance.now() - startTime;

      if (responseData.status !== 200 && responseData.status !== 201) {
        if (isClient && recordId) {
          try {
            useApiStore.getState().updateRecord(recordId, {
              status: "error",
              error: (responseData as any).error,
              response: responseData,
              duration,
            });
          } catch (e) {
            // Silently fail
          }
        }

        throw responseData;
      }

      if (isClient && recordId) {
        try {
          useApiStore.getState().updateRecord(recordId, {
            status: "success",
            response: responseData,
            duration,
          });
        } catch (e) {
          // Silently fail
        }
      }

      return responseData as APISuccessResponse<T>;
    }

    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });

    const responseData: { status: number; data: APIResponse<T> } =
      await response.json();

    const duration = performance.now() - startTime;

    if (responseData.data.status !== 200 && responseData.data.status !== 201) {
      // Log error
      if (isClient && recordId) {
        try {
          useApiStore.getState().updateRecord(recordId, {
            status: "error",
            error: (responseData.data as any).error,
            response: responseData.data,
            duration,
          });
        } catch (e) {
          // Silently fail
        }
      }

      // Error is logged in connectivity profiling, no toast needed
      throw responseData.data;
    }

    // Log success
    if (isClient && recordId) {
      try {
        useApiStore.getState().updateRecord(recordId, {
          status: "success",
          response: responseData.data,
          duration,
        });
      } catch (e) {
        // Silently fail
      }
    }

    return responseData.data as APISuccessResponse<T>;
  } catch (error) {
    const duration = performance.now() - startTime;

    // Log error for network failures
    if (isClient && recordId) {
      try {
        useApiStore.getState().updateRecord(recordId, {
          status: "error",
          error: error instanceof Error ? error.message : "Network error",
          duration,
        });
      } catch (e) {
        // Silently fail
      }
    }

    throw error;
  }
};

export default apiCall;
